import {
    createBrowserRouter,
    RouterProvider
} from 'react-router-dom';


import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@flexinet/ui-components/style.css'


import {UserAttributesContextProvider} from "./context/UserAttributesContext.tsx";
import {theme} from "./theme.ts";
import {MantineProvider} from "@mantine/core";
import {Notifications} from "@mantine/notifications";
import {ModalsProvider} from "@mantine/modals";

import {AuthProvider} from "./context/AuthContext.tsx";
import {actions} from "./config/rbac/actions.ts";
import permissions from "./config/permissions.ts";
import {useAuth} from "./hooks/useAuth.ts";
import {
    CognitoContextProvider,
    createAuthAwareQueryClient,
    SessionGuardProvider,
    VerifiedPermissionsProvider
} from '@flexinet/auth-components';
import {routesConfig} from "./config/routes.tsx";
import {QueryClientProvider} from '@tanstack/react-query';
import {Provider} from 'react-redux';
import {store} from './store';
import {ReactNode} from 'react';

const router = createBrowserRouter(routesConfig)
// FNP-865: auth errors from queries reach the SessionGuardProvider below, which
// tries a forced token refresh and only then redirects to sign in.
const queryClient = createAuthAwareQueryClient();

// FNP-1727: the shared provider does not read any AuthContext itself. The app
// owns session resolution and hands it in, so a signed-out visitor never
// reaches an AVP call (the old local provider threw `idToken is required
// parameter` there, unhandled). The provider re-evaluates when the user id
// changes; today that happens on the page load after a sign-in redirect, since
// AuthProvider resolves the session once per mount.
// actionType stays 'Action', the id the old local provider sent.
export const SessionVerifiedPermissionsProvider =({children}: { children: ReactNode }) => {
    const {status, user} = useAuth();

    return (
        <VerifiedPermissionsProvider
            actions={actions}
            policyStoreId={permissions.policyStoreId}
            region={permissions.region}
            actionType={'Action'}
            enabled={permissions.enabled}
            sessionStatus={status}
            sessionKey={status === 'authenticated' ? user?.userId : undefined}
        >
            {children}
        </VerifiedPermissionsProvider>
    );
};


// FNP-951: the Redux <Provider> sits here rather than in main.tsx (where the
// sibling apps put it) because main.tsx is owned by another initiative. It is
// the OUTERMOST wrapper, so every provider below can dispatch or read state.
// Redux has no ordering dependency on Mantine, the router or React Query —
// it neither consumes their context nor is consumed by them — so the nesting
// order is free and this placement is equivalent to wrapping <App/> itself.
export const App = () => {

    return (
        <Provider store={store}>
        <MantineProvider theme={theme}>
            <SessionGuardProvider>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <SessionVerifiedPermissionsProvider>
                        <UserAttributesContextProvider>
                            <CognitoContextProvider>
                                <ModalsProvider>
                                    <Notifications position={'bottom-left'}/>
                                    <RouterProvider router={router}/>
                                </ModalsProvider>
                            </CognitoContextProvider>
                        </UserAttributesContextProvider>
                    </SessionVerifiedPermissionsProvider>
                </AuthProvider>
            </QueryClientProvider>
            </SessionGuardProvider>
        </MantineProvider>
        </Provider>
    );
}

